import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { dashboardAPI, timeEntriesAPI, reportsAPI } from '../lib/api';
import { 
    FolderKanban, 
    ListTodo, 
    Users, 
    Clock, 
    DollarSign,
    ArrowRight,
    TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [recentEntries, setRecentEntries] = useState([]);
    const [projectSummaries, setProjectSummaries] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [statsRes, entriesRes, summaryRes] = await Promise.all([
                dashboardAPI.getStats(),
                timeEntriesAPI.getAll(),
                reportsAPI.getSummary()
            ]);
            setStats(statsRes.data);
            setRecentEntries(entriesRes.data.slice(0, 5));
            setProjectSummaries(summaryRes.data.slice(0, 3));
        } catch (error) {
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    // Animation class passed directly — no wrapper div needed
    const StatCard = ({ icon: Icon, label, value, color, href, animClass }) => (
        <Link
            to={href}
            className={`swiss-card group cursor-pointer ${animClass || ''}`}
            data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="caption mb-2">{label}</p>
                    <p className="kpi-number">{value}</p>
                </div>
                <div className={`w-12 h-12 ${color} rounded-sm flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
            </div>
            <div className="mt-4 flex items-center text-zinc-500 text-sm font-medium group-hover:text-orange-600 transition-colors">
                View all <ArrowRight className="w-4 h-4 ml-1" />
            </div>
        </Link>
    );

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-zinc-500">Loading...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8" data-testid="dashboard">
                {/* Header */}
                <div className="animate-fade-in">
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Dashboard
                    </h1>
                    <p className="text-lg text-zinc-500">
                        Overview of your time tracking activity
                    </p>
                </div>

                {/* Stats Grid — animation applied directly to each card, no wrapper divs */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard 
                        icon={FolderKanban} 
                        label="Projects" 
                        value={stats?.project_count || 0}
                        color="bg-orange-600"
                        href="/projects"
                        animClass="animate-fade-in stagger-1"
                    />
                    <StatCard 
                        icon={ListTodo} 
                        label="Tasks" 
                        value={stats?.task_count || 0}
                        color="bg-zinc-800"
                        href="/tasks"
                        animClass="animate-fade-in stagger-2"
                    />
                    <StatCard 
                        icon={Users} 
                        label="Team Members" 
                        value={stats?.team_member_count || 0}
                        color="bg-emerald-700"
                        href="/team"
                        animClass="animate-fade-in stagger-3"
                    />
                    <StatCard 
                        icon={Clock} 
                        label="Total Hours" 
                        value={stats?.total_hours?.toFixed(1) || 0}
                        color="bg-blue-600"
                        href="/time-entries"
                        animClass="animate-fade-in stagger-4"
                    />
                </div>

                {/* Earnings Card */}
                <div className="swiss-card animate-fade-in stagger-5" style={{ backgroundColor: '#18181b', color: 'white' }}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium tracking-wide uppercase mb-2" style={{ color: '#a1a1aa' }}>Total Earnings</p>
                            <p className="text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: 'white' }}>
                                ${stats?.total_earnings?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                            </p>
                        </div>
                        <div className="w-16 h-16 bg-orange-600 rounded-sm flex items-center justify-center flex-shrink-0">
                            <DollarSign className="w-8 h-8 text-white" />
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-emerald-400 text-sm font-medium">
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Based on {stats?.entry_count || 0} time entries
                    </div>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Recent Time Entries */}
                    <div className="swiss-card animate-fade-in">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Recent Entries
                            </h2>
                            <Link to="/time-entries">
                                <Button variant="ghost" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50">
                                    View all <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>

                        {recentEntries.length === 0 ? (
                            <div className="empty-state py-8">
                                <Clock className="empty-state-icon" />
                                <p className="text-zinc-500">No time entries yet</p>
                                <Link to="/time-entries">
                                    <Button className="mt-4 swiss-btn-primary" data-testid="add-first-entry-btn">
                                        Add First Entry
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {recentEntries.map((entry) => (
                                    <div 
                                        key={entry.id} 
                                        className="flex items-center justify-between p-4 bg-zinc-50 rounded-sm hover:bg-zinc-100 transition-colors"
                                        data-testid={`recent-entry-${entry.id}`}
                                    >
                                        <div>
                                            <p className="font-medium text-zinc-900">{entry.task_name}</p>
                                            <p className="text-sm text-zinc-500">{entry.project_name} • {entry.team_member_name}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-zinc-900">{entry.hours}h</p>
                                            <p className="text-sm text-emerald-600">${entry.total_cost?.toFixed(2)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Project Summaries */}
                    <div className="swiss-card animate-fade-in">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Top Projects
                            </h2>
                            <Link to="/reports">
                                <Button variant="ghost" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50">
                                    View all <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>

                        {projectSummaries.length === 0 ? (
                            <div className="empty-state py-8">
                                <FolderKanban className="empty-state-icon" />
                                <p className="text-zinc-500">No projects yet</p>
                                <Link to="/projects">
                                    <Button className="mt-4 swiss-btn-primary" data-testid="add-first-project-btn">
                                        Create First Project
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {projectSummaries.map((project) => (
                                    <div 
                                        key={project.project_id} 
                                        className="flex items-center justify-between p-4 bg-zinc-50 rounded-sm hover:bg-zinc-100 transition-colors"
                                        data-testid={`project-summary-${project.project_id}`}
                                    >
                                        <div>
                                            <p className="font-medium text-zinc-900">{project.project_name}</p>
                                            <p className="text-sm text-zinc-500">#{project.billing_number} • {project.task_count} tasks</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-zinc-900">{project.total_hours.toFixed(1)}h</p>
                                            <p className="text-sm text-emerald-600">${project.total_cost.toFixed(2)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
